import React, { useState, useEffect, useRef } from 'react';
import {
    MdSearch,
    MdNotificationsNone,
    MdFullscreen,
    MdFullscreenExit,
    MdLightMode,
    MdDarkMode,
    MdBookmarkBorder,
    MdTextFields
} from 'react-icons/md';
import { FiMoreVertical } from 'react-icons/fi';
import { LuPanelLeft, LuPanelRight } from "react-icons/lu";
import styles from './Topbar.module.css';

const Topbar = ({ toggleSidebar, isSidebarOpen, isMobile }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchFocused, setSearchFocused] = useState(false);
    const [showMobileSearch, setShowMobileSearch] = useState(false);
    const [user, setUser] = useState(null);
    const [showNotifications, setShowNotifications] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const notificationRef = useRef(null);
    const mobileSearchRef = useRef(null);

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) {
            setUser(JSON.parse(userData));
        }
    }, []);

    // Close notifications on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (notificationRef.current && !notificationRef.current.contains(event.target)) {
                setShowNotifications(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close mobile search when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (mobileSearchRef.current && !mobileSearchRef.current.contains(event.target)) {
                setShowMobileSearch(false);
            }
        };

        if (showMobileSearch) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showMobileSearch]);

    // Auto-focus search input on mobile when opened
    useEffect(() => {
        if (showMobileSearch && mobileSearchRef.current) {
            const input = mobileSearchRef.current.querySelector('input');
            if (input) {
                input.focus();
            }
        }
    }, [showMobileSearch]);

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            console.log('Searching for:', searchQuery);
            // Close mobile search after submit
            if (isMobile) {
                setShowMobileSearch(false);
            }
        }
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(err => {
                console.error('Error attempting to enable fullscreen:', err);
            });
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    const toggleTheme = () => {
        setIsDarkMode(!isDarkMode);
        // You can add localStorage persistence here
        // localStorage.setItem('theme', !isDarkMode ? 'dark' : 'light');
    };

    const toggleMobileSearch = () => {
        setShowMobileSearch(!showMobileSearch);
    };

    // Mock notifications (replace with real data)
    const notifications = [
        { id: 1, message: 'New user registered', time: '5 min ago', unread: true },
        { id: 2, message: 'Report generated successfully', time: '1 hour ago', unread: true },
        { id: 3, message: 'System maintenance scheduled', time: '2 hours ago', unread: false },
    ];

    const unreadCount = notifications.filter(n => n.unread).length;

    return (
        <header
            className={`${styles.topbar} ${isMobile
                ? styles.mobile
                : isSidebarOpen
                    ? styles.expanded
                    : styles.collapsed
                }`}
        >
            <div className={styles.topbarContent}>
                {/* Left Section - Toggle Button */}
                <div className={styles.leftSection}>
                    <button
                        className={styles.iconBtn}
                        onClick={toggleSidebar}
                        aria-label={isSidebarOpen ? "Close sidebar" : "Open sidebar"}
                        title={isSidebarOpen ? "Close Menu" : "Open Menu"}
                    >
                        {isSidebarOpen ? <LuPanelRight size={20} /> : <LuPanelLeft size={20} />}
                    </button>
                </div>

                {/* Right Section */}
                <div className={styles.rightSection}>
                    {/* Desktop Search Bar */}
                    {!isMobile && (
                        <form
                            onSubmit={handleSearch}
                            className={`${styles.searchBar} ${searchFocused ? styles.focused : ''}`}
                        >
                            <MdSearch className={styles.searchIcon} />
                            <input
                                type="text"
                                placeholder="Search...."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onFocus={() => setSearchFocused(true)}
                                onBlur={() => setSearchFocused(false)}
                                className={styles.searchInput}
                                aria-label="Search"
                            />
                        </form>
                    )}

                    {/* Action Icons */}
                    <div className={styles.actionIcons}>
                        {/* Mobile Search Toggle */}
                        {isMobile && (
                            <button
                                className={`${styles.iconBtn} ${showMobileSearch ? styles.active : ''}`}
                                onClick={toggleMobileSearch}
                                aria-label="Search"
                                title="Search"
                            >
                                <MdSearch />
                            </button>
                        )}

                        {/* Font Size */}
                        {/* <button
                            className={styles.iconBtn}
                            aria-label="Adjust font size"
                            title="Font Size"
                        >
                            <MdTextFields />
                        </button> */}

                        {/* Fullscreen */}
                        <button
                            className={styles.iconBtn}
                            onClick={toggleFullscreen}
                            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                        >
                            {isFullscreen ? <MdFullscreenExit /> : <MdFullscreen />}
                        </button>

                        {/* Theme Toggle */}
                        {/* <button
                            className={styles.iconBtn}
                            onClick={toggleTheme}
                            aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
                            title={isDarkMode ? "Light Mode" : "Dark Mode"}
                        >
                            {isDarkMode ? <MdLightMode /> : <MdDarkMode />}
                        </button> */}

                        {/* Bookmarks */}
                        {/* <button
                            className={styles.iconBtn}
                            aria-label="Bookmarks"
                            title="Bookmarks"
                        >
                            <MdBookmarkBorder />
                        </button> */}

                        {/* Notifications */}
                        {/* <div className={styles.notificationWrapper} ref={notificationRef}>
                            <button
                                className={`${styles.iconBtn} ${showNotifications ? styles.active : ''}`}
                                onClick={() => setShowNotifications(!showNotifications)}
                                aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
                                title="Notifications"
                            >
                                <MdNotificationsNone />
                                {unreadCount > 0 && (
                                    <span className={styles.badge} aria-label={`${unreadCount} unread notifications`}>
                                        {unreadCount}
                                    </span>
                                )}
                            </button>

                            {showNotifications && (
                                <div className={styles.dropdown} role="dialog" aria-label="Notifications">
                                    <div className={styles.dropdownHeader}>
                                        <h3>Notifications</h3>
                                        <span className={styles.notificationCount}>{unreadCount} new</span>
                                    </div>
                                    <div className={styles.dropdownBody}>
                                        {notifications.map(notification => (
                                            <div
                                                key={notification.id}
                                                className={`${styles.notificationItem} ${notification.unread ? styles.unread : ''}`}
                                                role="button"
                                                tabIndex={0}
                                                aria-label={`${notification.message}, ${notification.time}`}
                                            >
                                                <p className={styles.notificationMessage}>
                                                    {notification.message}
                                                </p>
                                                <span className={styles.notificationTime}>
                                                    {notification.time}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className={styles.dropdownFooter}>
                                        <button className={styles.viewAllBtn}>View all notifications</button>
                                    </div>
                                </div>
                            )}
                        </div> */}

                        {/* More Options */}
                        <button
                            className={styles.iconBtn}
                            aria-label="More options"
                            title="More"
                        >
                            <FiMoreVertical />
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Search Overlay */}
            {isMobile && showMobileSearch && (
                <div className={styles.mobileSearchOverlay} ref={mobileSearchRef}>
                    <form onSubmit={handleSearch} className={styles.mobileSearchBar}>
                        <MdSearch className={styles.searchIcon} />
                        <input
                            type="text"
                            placeholder="Search for an e-mail or task"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className={styles.searchInput}
                            aria-label="Search"
                        />
                        <button
                            type="button"
                            className={styles.closeSearchBtn}
                            onClick={() => setShowMobileSearch(false)}
                            aria-label="Close search"
                        >
                            ×
                        </button>
                    </form>
                </div>
            )}
        </header>
    );
};

export default Topbar;